import React, { useState } from 'react';
import { showError, showSuccess } from '../../utils/sweetAlert';

const PRINT_SIZES = [
  { value: 'small', label: 'Pequeño (10x10cm)' },
  { value: 'medium', label: 'Mediano (20x20cm)' },
  { value: 'large', label: 'Grande (30x30cm)' },
  { value: 'extra-large', label: 'Extra Grande (40x40cm)' }
];

const LUNCHERA_PRINT_SIZES = [
  { value: 'small', label: 'Chico' },
  { value: 'large', label: 'Grande' }
];

const APPAREL_POSITIONS = [
  { value: 'front', label: 'Frente', icon: 'fa-shirt', marker: 'front' },
  { value: 'back', label: 'Espalda', icon: 'fa-shirt', marker: 'back' },
  { value: 'left-sleeve', label: 'Manga izquierda', icon: 'fa-hand', marker: 'left-sleeve' },
  { value: 'right-sleeve', label: 'Manga derecha', icon: 'fa-hand', marker: 'right-sleeve' },
  { value: 'pocket', label: 'Pecho / bolsillo', icon: 'fa-square', marker: 'pocket' }
];

const SHIRT_POSITIONS = [
  { value: 'pocket', label: 'Pecho', icon: 'fa-square', marker: 'pocket' },
  { value: 'back', label: 'Espalda', icon: 'fa-shirt', marker: 'back' }
];

const APPAREL_POSITION_VIEWS = {
  front: 'front',
  pocket: 'front',
  'left-sleeve': 'front',
  'right-sleeve': 'front',
  back: 'back'
};

const APPAREL_ZONE_DETAILS = {
  front: { label: 'Frente central', description: 'Diseño principal', icon: 'fa-shirt' },
  back: { label: 'Espalda central', description: 'Diseño principal', icon: 'fa-shirt' },
  'left-sleeve': { label: 'Manga izquierda', description: 'Logo o detalle', icon: 'fa-chevron-left' },
  'right-sleeve': { label: 'Manga derecha', description: 'Logo o detalle', icon: 'fa-chevron-right' },
  pocket: { label: 'Pecho / bolsillo', description: 'Logo pequeño', icon: 'fa-square' }
};

const SHIRT_ZONE_DETAILS = {
  pocket: { label: 'Pecho', description: 'Logo o detalle', icon: 'fa-square' },
  back: { label: 'Espalda', description: 'Diseño principal', icon: 'fa-shirt' }
};

const PILLOW_ZONE_DETAILS = {
  front: { label: 'Frente completo', description: 'Cara principal', icon: 'fa-couch' },
  back: { label: 'Dorso completo', description: 'Cara trasera', icon: 'fa-couch' }
};

const LUNCHERA_ZONE_DETAILS = {
  front: { label: 'Frente', description: 'Cara principal', icon: 'fa-bag-shopping' },
  back: { label: 'Dorso', description: 'Cara trasera', icon: 'fa-bag-shopping' }
};

const APPAREL_MOCKUPS = {
  tshirt: {
    front: '/images/customization/tshirt-front-black.webp',
    back: '/images/customization/tshirt-back-black.webp'
  },
  'tshirt-polyester': {
    front: '/images/customization/tshirt-front-white-polyester.webp',
    back: '/images/customization/tshirt-back-white-polyester.webp'
  },
  hoodie: {
    front: '/images/customization/hoodie-front-neutral.webp',
    back: '/images/customization/hoodie-back-neutral.webp'
  },
  pillow: {
    front: '/images/customization/pillow-front-neutral.webp',
    back: '/images/customization/pillow-back-neutral.webp'
  },
  lunchera: {
    front: '/images/customization/lunchera-front.png',
    back: '/images/customization/lunchera-back.png'
  }
};

const getMerchandiseProfile = (product) => {
  const category = typeof product?.category === 'object' ? product.category?.name : product?.category;
  const text = `${product?.name || ''} ${category || ''} ${product?.subcategory || ''}`.toLowerCase();

  if (text.includes('lanyard') || text.includes('llavero')) {
    return { key: 'simple', icon: 'fa-tags', title: 'Tu logo o diseño', description: 'Subí el archivo que querés aplicar. Si necesitás aclarar un detalle, podés dejar una nota.', positions: [], defaultPositions: [], showPrintOptions: false };
  }
  if (text.includes('cuadro')) {
    return { key: 'flat', icon: 'fa-image', title: 'Tu imagen para el cuadro', description: 'Subí la imagen final que querés imprimir. La adaptaremos al formato del cuadro.', positions: [], defaultPositions: ['full'], showPrintOptions: false };
  }
  if (text.includes('almohad')) {
    return { key: 'pillow', visualKey: 'pillow', icon: 'fa-couch', title: 'Tu diseño para el almohadón', description: 'Elegí una o ambas caras y mirá cómo quedará ubicado tu diseño.', positions: [{ value: 'front', label: 'Frente', icon: 'fa-couch', marker: 'front' }, { value: 'back', label: 'Dorso', icon: 'fa-couch', marker: 'back' }], defaultPositions: ['front'], showPrintOptions: true };
  }
  if (text.includes('lunchera')) {
    return { key: 'lunchera', visualKey: 'lunchera', icon: 'fa-bag-shopping', title: 'Tu diseño para la lunchera', description: 'Elegí la cara y el tamaño del estampado para tu lunchera.', positions: [{ value: 'front', label: 'Frente', icon: 'fa-bag-shopping', marker: 'front' }, { value: 'back', label: 'Dorso', icon: 'fa-bag-shopping', marker: 'back' }], zoneDetails: LUNCHERA_ZONE_DETAILS, defaultPositions: ['front'], printSizes: LUNCHERA_PRINT_SIZES, showPrintOptions: true };
  }
  if (text.includes('bolsa') || text.includes('bolza') || text.includes('bolso') || text.includes('tote') || text.includes('morral')) {
    return { key: 'bag', icon: 'fa-bag-shopping', title: 'Tu diseño para la bolsa', description: 'Elegí las caras de la bolsa que querés personalizar y subí el diseño.', positions: [{ value: 'front', label: 'Frente', icon: 'fa-bag-shopping', marker: 'front' }, { value: 'back', label: 'Dorso', icon: 'fa-bag-shopping', marker: 'back' }, { value: 'full', label: 'Completo', icon: 'fa-expand', marker: 'full' }], defaultPositions: ['front'], showPrintOptions: true };
  }
  if (text.includes('gorro')) {
    return { key: 'cap', icon: 'fa-hat-cowboy', title: 'Tu logo para el gorro', description: 'Elegí dónde querés aplicar el logo y adjuntá el diseño.', positions: [{ value: 'front', label: 'Frente', icon: 'fa-hat-cowboy', marker: 'front' }, { value: 'left-side', label: 'Lateral izquierdo', icon: 'fa-hat-cowboy', marker: 'left-side' }, { value: 'right-side', label: 'Lateral derecho', icon: 'fa-hat-cowboy', marker: 'right-side' }, { value: 'back', label: 'Atrás', icon: 'fa-hat-cowboy', marker: 'back' }], defaultPositions: ['front'], showPrintOptions: true };
  }
  if (text.includes('camisa')) {
    return { key: 'hoodie', visualKey: 'hoodie', icon: 'fa-shirt', title: 'Tu imagen para el estampado', description: 'Subí el diseño y elegí si querés aplicarlo en el pecho, la espalda o ambos.', positions: SHIRT_POSITIONS, zoneDetails: SHIRT_ZONE_DETAILS, defaultPositions: ['pocket'], showPrintOptions: true };
  }
  if (text.includes('canguro') || text.includes('campera')) {
    const garmentName = text.includes('campera') ? 'campera' : 'canguro';
    return { key: 'hoodie', visualKey: 'hoodie', icon: 'fa-shirt', title: 'Tu imagen para el estampado', description: `Subí el diseño y elegí visualmente las zonas de tu ${garmentName} donde querés aplicarlo.`, positions: APPAREL_POSITIONS, defaultPositions: ['front'], showPrintOptions: true };
  }
  if (text.includes('buzo')) {
    return { key: 'sweatshirt', icon: 'fa-shirt', title: 'Tu imagen para el estampado', description: 'Subí el diseño y elegí visualmente las zonas de tu buzo donde querés aplicarlo.', positions: APPAREL_POSITIONS, defaultPositions: ['front'], showPrintOptions: true };
  }
  if (text.includes('remera')) {
    const isPolyester = text.includes('polyester') || text.includes('poliester') || text.includes('poliéster');
    return { key: 'tshirt', visualKey: isPolyester ? 'tshirt-polyester' : 'tshirt', icon: 'fa-shirt', title: 'Tu imagen para el estampado', description: 'Subí el diseño y elegí visualmente las zonas de tu remera donde querés aplicarlo.', positions: APPAREL_POSITIONS, defaultPositions: ['front'], showPrintOptions: true };
  }
  return { key: 'default', icon: 'fa-box', title: 'Tu diseño', description: 'Subí la imagen que querés aplicar a este producto.', positions: [{ value: 'front', label: 'Frente', icon: 'fa-box', marker: 'front' }, { value: 'back', label: 'Dorso', icon: 'fa-box', marker: 'back' }, { value: 'full', label: 'Completo', icon: 'fa-expand', marker: 'full' }], defaultPositions: ['front'], showPrintOptions: true };
};

const ApparelPlacementDiagram = ({ garmentType, view, positions, selectedPositions, imagePreview, onTogglePosition }) => {
  const visiblePositions = positions.filter(position => APPAREL_POSITION_VIEWS[position.value] === view);
  const viewLabel = view === 'front' ? 'frontal' : garmentType === 'pillow' ? 'del dorso' : 'trasera';

  return (
    <div className={`apparel-placement-diagram apparel-placement-${garmentType} apparel-placement-${view}`}>
      <div className="apparel-view-caption" aria-live="polite">Vista {viewLabel}</div>
      <div className="apparel-diagram-canvas">
        <img
          className="apparel-garment-mockup"
          src={APPAREL_MOCKUPS[garmentType][view]}
          alt=""
          aria-hidden="true"
        />
        {visiblePositions.map(position => {
          const isSelected = selectedPositions.includes(position.value);
          const hasPreview = isSelected && imagePreview;

          return (
            <button
              key={position.value}
              type="button"
              className={`garment-zone garment-zone-${position.value} ${isSelected ? 'selected' : ''} ${hasPreview ? 'has-preview' : ''}`}
              style={hasPreview ? { backgroundImage: `url(${imagePreview})` } : undefined}
              onClick={() => onTogglePosition(position.value)}
              aria-label={`${isSelected ? 'Quitar' : 'Seleccionar'} ${position.label}`}
              aria-pressed={isSelected}
            >
              <span className="garment-zone-marker" aria-hidden="true">
                <i className={`fa-solid ${isSelected ? 'fa-check' : 'fa-plus'}`}></i>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const CustomOrderForm = ({ product, selectedVariant, onClose, initialQuantity = 1 }) => {
  const profile = getMerchandiseProfile(product);
  const hasEnhancedPlacementDiagram = profile.key === 'hoodie' || profile.key === 'tshirt' || profile.key === 'pillow' || profile.key === 'lunchera';
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    instructions: '',
    printPositions: profile.defaultPositions,
    printSize: profile.printSizes?.[0]?.value || 'medium',
    quantity: initialQuantity
  });
  const [customImage, setCustomImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [activeGarmentView, setActiveGarmentView] = useState('front');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const togglePrintPosition = (position) => {
    setFormData(previous => {
      const isSelected = previous.printPositions.includes(position);
      const printPositions = isSelected
        ? previous.printPositions.filter(item => item !== position)
        : [...previous.printPositions, position];
      return { ...previous, printPositions: printPositions.length > 0 ? printPositions : previous.printPositions };
    });
  };

  const removeApparelPosition = (position) => {
    if (formData.printPositions.length <= 1) return;
    setActiveGarmentView(APPAREL_POSITION_VIEWS[position] || 'front');
    togglePrintPosition(position);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        showError('Solo se permiten imágenes (JPEG, PNG, WebP, GIF)');
        return;
      }
      
      // Validar tamaño (10MB máximo)
      if (file.size > 10 * 1024 * 1024) {
        showError('La imagen no puede superar los 10MB');
        return;
      }

      setCustomImage(file);
      
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.customerName.trim()) {
      showError('Por favor ingresa tu nombre');
      return;
    }
    if (!formData.customerEmail.trim()) {
      showError('Por favor ingresa tu email');
      return;
    }
    if (!customImage) {
      showError('Por favor sube una imagen para el estampado');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('productId', product._id);
      submitData.append('customerName', formData.customerName);
      submitData.append('customerEmail', formData.customerEmail);
      submitData.append('customerPhone', formData.customerPhone);
      submitData.append('instructions', formData.instructions);
      submitData.append('printPosition', formData.printPositions[0] || 'other');
      submitData.append('printPositions', JSON.stringify(formData.printPositions));
      submitData.append('printSize', profile.showPrintOptions ? formData.printSize : 'custom');
      submitData.append('quantity', formData.quantity);
      submitData.append('customImage', customImage);
      
      if (selectedVariant) {
        submitData.append('selectedSize', selectedVariant.size || '');
        submitData.append('selectedColor', selectedVariant.color || '');
        submitData.append('selectedSku', selectedVariant.sku || '');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/custom-orders`, {
        method: 'POST',
        body: submitData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error al enviar el pedido');
      }

      showSuccess('¡Solicitud enviada! Te contactaremos pronto con la cotización y los detalles.');
      
      // Limpiar formulario
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        instructions: '',
        printPositions: profile.defaultPositions,
        printSize: profile.printSizes?.[0]?.value || 'medium',
        quantity: initialQuantity
      });
      setCustomImage(null);
      setImagePreview(null);
      
      if (onClose) onClose();

    } catch (error) {
      console.error('Error:', error);
      showError(error.message || 'Error al enviar el pedido');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="custom-order-form-container">
      <div className="custom-order-header">
        <h3>
          <i className="fa-solid fa-pen-ruler me-2"></i>
          Personaliza tu Producto
        </h3>
        <p>{profile.description}</p>
      </div>

      <form onSubmit={handleSubmit} className="custom-order-form">
        {/* Sección de imagen */}
        <div className="form-section">
          <h4>
            <i className={`fa-solid ${profile.icon} me-2`}></i>
            {profile.title}
          </h4>
          
          <div className="image-upload-area">
            {imagePreview ? (
              <div className="image-preview">
                <img src={imagePreview} alt="Preview" />
                <button 
                  type="button" 
                  className="remove-image-btn"
                  onClick={() => {
                    setCustomImage(null);
                    setImagePreview(null);
                  }}
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>
            ) : (
              <label className="upload-label">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageChange}
                  hidden
                />
                <div className="upload-placeholder">
                  <i className="fa-solid fa-cloud-upload-alt"></i>
                  <span>Haz clic o arrastra tu imagen aquí</span>
                  <small>JPEG, PNG, WebP o GIF (máx. 10MB)</small>
                </div>
              </label>
            )}
          </div>

          {/* Botón para crear con AI */}
          <div className="ai-generator-link">
            <span>¿No tienes una imagen?</span>
            <a 
              href="https://ai.rementerprises.uy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="ai-link-btn"
            >
              <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
              Crear con Inteligencia Artificial
            </a>
          </div>
        </div>

        {/* Opciones de estampado */}
        {profile.showPrintOptions && <div className="form-section print-options-section">
          <div className="print-options-heading">
            <div>
              <h4><i className="fa-solid fa-wand-magic-sparkles me-2"></i>Armá tu estampado</h4>
              <p>Elegí una o más ubicaciones. Vamos a revisar tu diseño antes de producirlo.</p>
            </div>
            <span className="print-selection-count">{formData.printPositions.length} {formData.printPositions.length === 1 ? 'zona' : 'zonas'}</span>
          </div>

          {hasEnhancedPlacementDiagram ? (
            <>
              <div className="apparel-view-tabs" role="tablist" aria-label="Vista del producto">
                <button type="button" role="tab" aria-selected={activeGarmentView === 'front'} className={activeGarmentView === 'front' ? 'active' : ''} onClick={() => setActiveGarmentView('front')}>
                  <i className={`fa-solid ${profile.icon} me-2`}></i>Frente
                </button>
                <button type="button" role="tab" aria-selected={activeGarmentView === 'back'} className={activeGarmentView === 'back' ? 'active' : ''} onClick={() => setActiveGarmentView('back')}>
                  <i className="fa-solid fa-arrow-rotate-right me-2"></i>{profile.key === 'pillow' ? 'Dorso' : 'Espalda'}
                </button>
              </div>

              <ApparelPlacementDiagram
                garmentType={profile.visualKey || profile.key}
                view={activeGarmentView}
                positions={profile.positions}
                selectedPositions={formData.printPositions}
                imagePreview={imagePreview}
                onTogglePosition={togglePrintPosition}
              />

              <div className="apparel-zone-picker" aria-label={`Zonas disponibles en la vista ${activeGarmentView === 'front' ? 'frontal' : 'trasera'}`}>
                {profile.positions.filter(position => APPAREL_POSITION_VIEWS[position.value] === activeGarmentView).map(position => {
                  const details = profile.zoneDetails?.[position.value] || (profile.key === 'pillow' ? PILLOW_ZONE_DETAILS[position.value] : APPAREL_ZONE_DETAILS[position.value]);
                  const isSelected = formData.printPositions.includes(position.value);

                  return (
                    <button
                      key={position.value}
                      type="button"
                      className={`apparel-zone-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => togglePrintPosition(position.value)}
                      aria-pressed={isSelected}
                    >
                      <span className="apparel-zone-option-icon"><i className={`fa-solid ${details.icon}`}></i></span>
                      <span className="apparel-zone-option-copy"><strong>{details.label}</strong><small>{details.description}</small></span>
                      <i className={`fa-solid ${isSelected ? 'fa-circle-check' : 'fa-circle-plus'} apparel-zone-option-state`}></i>
                    </button>
                  );
                })}
              </div>

              <div className="selected-placement-summary">
                <span className="selected-placement-label">Zonas seleccionadas</span>
                <div className="selected-placement-tags">
                  {profile.positions.filter(position => formData.printPositions.includes(position.value)).map(position => (
                    <button
                      key={position.value}
                      type="button"
                      className="selected-placement-tag"
                      onClick={() => removeApparelPosition(position.value)}
                      disabled={formData.printPositions.length === 1}
                      aria-label={`Quitar ${position.label}`}
                      title={formData.printPositions.length === 1 ? 'Seleccioná otra zona antes de quitar esta' : `Quitar ${position.label}`}
                    >
                      {position.label}<i className="fa-solid fa-times"></i>
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={`placement-visual placement-visual-${profile.key}`}>
                <div className={`placement-garment placement-garment-${profile.key}`}>
                  {profile.key === 'hoodie' ? <><span className="hoodie-hood"></span><span className="hoodie-sleeve hoodie-sleeve-left"></span><span className="hoodie-sleeve hoodie-sleeve-right"></span><span className="hoodie-body"></span></> : profile.key === 'bag' ? <><span className="bag-handle bag-handle-left"></span><span className="bag-handle bag-handle-right"></span><span className="bag-body"></span></> : profile.key === 'cap' ? <><span className="cap-crown"></span><span className="cap-brim"></span></> : <i className={`fa-solid ${profile.icon}`}></i>}
                  {profile.positions.map(position => <button key={position.value} type="button" className={`placement-marker placement-marker-${position.marker} ${formData.printPositions.includes(position.value) ? 'selected' : ''}`} onClick={() => togglePrintPosition(position.value)} aria-label={`Seleccionar ${position.label}`} aria-pressed={formData.printPositions.includes(position.value)}><i className="fa-solid fa-plus"></i></button>)}
                </div>
              </div>

              <div className="position-options position-options-profile">
                {profile.positions.map(position => <button key={position.value} type="button" className={`position-btn ${formData.printPositions.includes(position.value) ? 'selected' : ''}`} onClick={() => togglePrintPosition(position.value)} aria-pressed={formData.printPositions.includes(position.value)}><i className={`fa-solid ${position.icon}`}></i><span>{position.label}</span><small>{formData.printPositions.includes(position.value) ? 'Seleccionado' : 'Agregar'}</small></button>)}
              </div>
            </>
          )}

          <div className="form-row print-size-row">
            <div className="form-group">
              <label>Tamaño aproximado del estampado</label>
              <select name="printSize" value={formData.printSize} onChange={handleInputChange} className="form-select">
                {(profile.printSizes || PRINT_SIZES).map(size => <option key={size.value} value={size.value}>{size.label}</option>)}
              </select>
            </div>
          </div>
        </div>}

        <div className="form-section quantity-section">
          <div>
            <h4><i className="fa-solid fa-boxes-stacked me-2"></i>Cantidad</h4>
            <p>{profile.showPrintOptions ? 'La cotización final se confirma según las zonas seleccionadas y la cantidad.' : 'Indicá cuántas unidades necesitás para prepararte la cotización.'}</p>
          </div>
          <div className="quantity-input">
            <button type="button" onClick={() => setFormData(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))} aria-label="Restar una unidad"><i className="fa-solid fa-minus"></i></button>
            <span>{formData.quantity}</span>
            <button type="button" onClick={() => setFormData(prev => ({ ...prev, quantity: prev.quantity + 1 }))} aria-label="Sumar una unidad"><i className="fa-solid fa-plus"></i></button>
          </div>
        </div>

        {/* Datos de contacto */}
        <div className="form-section">
          <h4>
            <i className="fa-solid fa-user me-2"></i>
            Tus Datos de Contacto
          </h4>
          
          <div className="form-row">
            <div className="form-group">
              <label>Nombre Completo *</label>
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleInputChange}
                placeholder="Tu nombre"
                required
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="customerEmail"
                value={formData.customerEmail}
                onChange={handleInputChange}
                placeholder="tu@email.com"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Teléfono (opcional)</label>
              <input
                type="tel"
                name="customerPhone"
                value={formData.customerPhone}
                onChange={handleInputChange}
                placeholder="+598 99 123 456"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Instrucciones Adicionales (opcional)</label>
            <textarea
              name="instructions"
              value={formData.instructions}
              onChange={handleInputChange}
              placeholder="Ej: Quiero el logo centrado, con fondo transparente..."
              rows={3}
            />
          </div>
        </div>

        {/* Resumen y botón de envío */}
        <div className="form-section summary-section">
          <div className="order-summary">
            <div className="summary-item">
              <span>Producto:</span>
              <strong>{product.name}</strong>
            </div>
            {selectedVariant && (
              <div className="summary-item">
                <span>Variante:</span>
                <strong>{selectedVariant.size} - {selectedVariant.color}</strong>
              </div>
            )}
            <div className="summary-item">
              <span>Cantidad:</span>
              <strong>{formData.quantity}</strong>
            </div>
            <div className="summary-item total">
              <span>Cotización:</span>
              <strong>A confirmar</strong>
            </div>
            <p className="quote-summary-note">
              El precio final depende del tipo de producto, la cantidad y los estampados elegidos.
            </p>
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting || !customImage}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin me-2"></i>
                Enviando consulta...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane me-2"></i>
                Solicitar cotización
              </>
            )}
          </button>

          <p className="form-note">
            <i className="fa-solid fa-info-circle me-1"></i>
            Te contactaremos para confirmar los detalles y el pago antes de procesar tu pedido.
          </p>
        </div>
      </form>
    </div>
  );
};

export default CustomOrderForm;
